import React from 'react';
import clsx from 'clsx';
import Icon from '@components/atoms/icon/icon';
import { SellerInformationContext } from '@context/seller-information.context';
import { useContextSelector } from 'use-context-selector';
import useUser from '@hooks/user/use-user';
import { useQueryClient } from '@tanstack/react-query';
import { SellerInformationKeys } from '@enums/react-query-keys';
import { useTranslation } from 'next-i18next';
import { IFlattenedSellerInformation } from '@interfaces/models/seller-info/seller-information';
import useWindowSize from '@hooks/use-window-size';
import { ErrorMessage, Field } from 'formik';
import SellerFormTextInput from '../form-components/seller-form-text-input';
import TaxInformation from '../form-components/tax-information';
import PersonalInformation from '../form-components/personal-information';
import AddressInformation from '../form-components/address-information';
import CountriesMultiSelect from '../form-components/countries-multi-select/countires-multi-select';
import styles from './seller-information-form.module.scss';

const SellerInformationContent = ({ values, errors, touched, dirty }) => {
  const { t } = useTranslation('get-paid');
  const { user } = useUser();
  const { isWiderThanMd } = useWindowSize();
  const queryClient = useQueryClient();
  const handleEstablishedSelectedCountries = useContextSelector(
    SellerInformationContext,
    (value) => value.handleEstablishedSelectedCountries,
  );
  const userFlags = useContextSelector(SellerInformationContext, (value) => value.userFlags);
  const countries = useContextSelector(SellerInformationContext, (value) => value.countries);
  const taxInformation: IFlattenedSellerInformation = queryClient.getQueryData([
    SellerInformationKeys.GET_TAX_INFORMATION,
    user?.id,
  ]);

  const ErrorLabel = (errorMessage: string): React.ReactElement => {
    return (
      <div className={clsx('vc-inputText-errorMsg vc-text-xs', styles.labelErrorContainer)}>
        <span className={styles.labelErrorContainer__icon}>
          <Icon
            name="warning-20"
            data-cy="error_info_icon"
            ariaHidden
          />
        </span>
        {t(errorMessage)}
      </div>
    );
  };

  return (
    <>
      {/* Tax Information  */}
      <TaxInformation
        values={values}
        errors={errors}
        touched={touched}
        dirty={dirty}
        ErrorLabel={ErrorLabel}
      />
      {/* Personal Information  */}
      {(userFlags?.isUSC2CUser || userFlags?.isEUC2CUser) && (
        <PersonalInformation
          values={values}
          errors={errors}
          dirty={dirty}
          touched={touched}
        />
      )}
      {/* Business Information  */}
      {(userFlags?.isUSB2CUser || userFlags?.isEUB2CUser) && (
        <div className={clsx(styles.formGroup, styles['formGroup--formGroupFlexContainer'])}>
          <div
            className={clsx(
              styles.formTitle,
              styles['formTitle--infoTitle'],
              isWiderThanMd ? 'vc-text-H24' : 'vc-text-H20',
              'vc-text-bold',
            )}
          >
            {t('GET_PAID.TAX_INFORMATION_FORM.BUSINESS_INFO_SUBTITLE', 'Business Information')}
          </div>
          <div
            className={clsx('vc-formUnit', styles['vc-formUnit'], {
              [styles.error + ' error']: errors.businessName && touched.businessName,
            })}
          >
            <SellerFormTextInput
              name="businessName"
              label={t('GET_PAID.PERSONAL_INFORMATION_FORM.LABEL.BUSINESS_NAME')}
              value={values.businessName}
              placeholder={t('GET_PAID.PERSONAL_INFORMATION_FORM.PLACEHOLDER.BUSINESS_NAME')}
              required
              dirty={dirty}
              touched={touched}
              maxLength={100}
              t={t}
            />
          </div>
        </div>
      )}
      {/* Address Information  */}
      <AddressInformation
        values={values}
        errors={errors}
        touched={touched}
        ErrorLabel={ErrorLabel}
        dirty={dirty}
      />
      {/* Country Multi Select Dropdown */}
      {userFlags?.isEUB2CUser && (
        <div
          className={clsx(
            styles.formGroup,
            styles['formGroup--formGroupFlexContainer'],
            styles['formGroup--formGroupFlexContainer--optionalCountries'],
          )}
        >
          <div
            className={clsx(
              'vc-formUnit',
              'vc-formUnit--select',
              styles['vc-formUnit'],
              styles['vc-formUnit--select'],
              {
                [styles.error + ' error']: errors.country && touched.country,
              },
            )}
          >
            <CountriesMultiSelect
              countries={countries}
              preSelectedCountries={taxInformation?.establishmentCountries}
              onSelectedCountries={handleEstablishedSelectedCountries}
            />
            <label
              id="label-country"
              className={`vc-inputText-label ${styles['vc-inputText-label']}`}
            >
              {t(
                'GET_PAID.TAX_INFORMATION_FORM.EXTRA_COUNTRY_FIELD',
                'Countries of permanent establishment (optional)',
              )}
            </label>
          </div>
          <div className={clsx('vc-text-H12', styles['formGroup--formGroupFlexContainer--optionalCountries__desc'])}>
            {t(
              'GET_PAID.TAX_INFORMATION_FORM.EXTRA_COUNTRY_DESCRIPTION',
              'If your company has a permanent establishment in an EU state other than the country of tax residency.',
            )}
          </div>
        </div>
      )}

      {/* only US 1099K */}
      {userFlags?.isUSUser && (
        <div
          className={clsx(
            'vc-formUnit',
            'vc-formUnit--select',
            styles['vc-formUnit'],
            styles['vc-formUnit--select'],
            {
              [styles.error + ' error']: errors?.isElectronically && touched?.isElectronically,
            },
            { [styles['vc-inputText--touched']]: dirty && touched?.isElectronically },
          )}
        >
          <Field
            as="select"
            name="isElectronically"
            aria-labelledby="label-1099k"
            className={clsx('vc-inputText', styles['vc-inputText'], {
              [styles['vc-inputText--touched']]: dirty && touched?.isElectronically,
            })}
            value={values.isElectronically}
            required
          >
            <option value=""></option>
            <option value="true">{t('GET_PAID.ELECTRONICALLY.YES')}</option>
            <option value="false">{t('GET_PAID.ELECTRONICALLY.NO')}</option>
          </Field>
          <label
            id="label-1099k"
            className={`vc-inputText-label ${styles['vc-inputText-label']} ${styles.required}`}
          >
            {t('GET_PAID.ELECTRONICALLY.US')}
          </label>
          <ErrorMessage
            name="isElectronically"
            render={ErrorLabel}
          />
        </div>
      )}
    </>
  );
};

export default SellerInformationContent;
